import moment from 'moment';

class ExpenseImportController {
  constructor($rootScope, $http, $state, toastr, AuthenticationService, TravelExpenseService) {
    'ngInject';
    this.$rootScope = $rootScope;
    this.$http = $http;
    this.$state = $state;
    this.toastr = toastr;
    this.auth = AuthenticationService;
    this.travelExpenseService = TravelExpenseService;
    this.maxStatusDate = moment().format('YYYY-MM-DD');
    this.statusDateSequence = [
      'submitted',
      'approved',
      'reimbursementRequested',
      'reimbursementApproved',
      'processed'
    ];

    this.loadingUsers = true;
    this.loadingProjects = false;
    this.creating = false;
    this.users = [];
    this.projects = [];
    this.selectedUser = '';
    this.selectedUserLabel = '';
    this.reportTypes = [
      { value: 'TRAVEL', label: 'Travel' },
      { value: 'GENERAL_BUSINESS', label: 'General Business' },
      { value: 'OTHER_DIRECT_COSTS', label: 'Other Direct Costs' }
    ];

    this.form = {
      userId: null,
      reportType: 'TRAVEL',
      name: '',
      purpose: '',
      project: null,
      description: '',
      statusDates: {
        submitted: null,
        approved: null,
        reimbursementRequested: null,
        reimbursementApproved: null,
        processed: null
      }
    };
    this.statusDateText = {
      submitted: '',
      approved: '',
      reimbursementRequested: '',
      reimbursementApproved: '',
      processed: ''
    };
    this.statusDateTextUpdating = {};
    this.statusDateMins = {
      submitted: null,
      approved: null,
      reimbursementRequested: null,
      reimbursementApproved: null,
      processed: null
    };

    this.updateStatusDateFields();
    this.loadUsers();
  }

  loadUsers() {
    let self = this;
    this.loadingUsers = true;
    // Omit active filter so inactive employees are included.
    this.$http.get(this.$rootScope.apiHost + '/api/admin/users/names', { withCredentials: true })
      .then(function (res) {
        self.users = (res.data || []).map(function (user) {
          let name = user.fullName || ((user.firstName || '') + ' ' + (user.lastName || '')).trim();
          if (user.active === false) {
            name = name + ' (inactive)';
          }
          user.fullName = name;
          return user;
        }).sort(function (a, b) {
          return (a.fullName || '').localeCompare(b.fullName || '');
        });
      })
      .catch(function () {
        self.toastr.error('Could not load employees.');
      })
      .finally(function () {
        self.loadingUsers = false;
      });
  }

  selectUser(user) {
    if (!user) {
      return;
    }
    this.form.userId = user.id;
    this.selectedUserLabel = user.fullName;
    this.selectedUser = user.fullName;
    this.form.project = null;
    this.loadProjectsForUser(user.id);
  }

  clearSelectedUser() {
    this.form.userId = null;
    this.selectedUser = '';
    this.selectedUserLabel = '';
    this.form.project = null;
    this.projects = [];
  }

  loadProjectsForUser(userId) {
    let self = this;
    this.loadingProjects = true;
    this.projects = [];
    this.travelExpenseService.getExpenseProjectOptionsForUser(userId)
      .then(function (projects) {
        self.projects = projects;
      })
      .catch(function () {
        self.projects = [];
        self.toastr.error('Could not load projects for this employee.');
      })
      .finally(function () {
        self.loadingProjects = false;
      });
  }

  needsProject() {
    if (this.form.reportType === 'OTHER_DIRECT_COSTS') {
      return true;
    }
    return this.form.reportType === 'TRAVEL' && this.form.purpose === 'customer';
  }

  onPurposeChange() {
    if (this.form.purpose !== 'customer') {
      this.form.project = null;
    }
  }

  onReportTypeChange() {
    if (this.form.reportType === 'OTHER_DIRECT_COSTS') {
      this.form.purpose = 'customer';
    } else if (this.form.reportType === 'GENERAL_BUSINESS') {
      this.form.purpose = '';
      this.form.project = null;
    } else if (this.form.purpose !== 'customer') {
      this.form.project = null;
    }
    this.updateStatusDateFields();
  }

  updateStatusDateFields() {
    let reportLabel = 'expense report';
    if (this.form.reportType === 'TRAVEL') {
      reportLabel = 'Travel Request';
    } else if (this.form.reportType === 'OTHER_DIRECT_COSTS') {
      reportLabel = 'other direct costs report';
    } else if (this.form.reportType === 'GENERAL_BUSINESS') {
      reportLabel = 'general business expense report';
    }

    this.statusDateFields = [
      {
        key: 'submitted',
        label: 'When was the ' + reportLabel + ' first submitted?',
        minDate: null
      },
      {
        key: 'approved',
        label: 'When was the ' + reportLabel + ' approved?',
        minDate: null
      },
      {
        key: 'reimbursementRequested',
        label: 'When was reimbursement first requested?',
        minDate: null
      },
      {
        key: 'reimbursementApproved',
        label: 'When was reimbursement approved?',
        minDate: null
      },
      {
        key: 'processed',
        label: 'When was reimbursement processed?',
        minDate: null
      }
    ];

    this.statusDateFieldMap = {};
    this.statusDateFields.forEach((field) => {
      this.statusDateFieldMap[field.key] = field;
    });

    this.requestStatusDateFields = [
      this.statusDateFieldMap.submitted,
      this.statusDateFieldMap.approved
    ];
    this.reimbursementStatusDateFields = [
      this.statusDateFieldMap.reimbursementRequested,
      this.statusDateFieldMap.reimbursementApproved,
      this.statusDateFieldMap.processed
    ];

    this.updateStatusDateMinDates();
  }

  updateStatusDateMinDates() {
    if (!this.statusDateFields) {
      return;
    }

    if (!this.statusDateMins) {
      this.statusDateMins = {};
    }

    this.statusDateFields.forEach((field) => {
      const minDate = this.getStatusDateMinDate(field.key);
      field.minDate = minDate;
      this.statusDateMins[field.key] = minDate;
    });
  }

  onStatusDateSelected(key, selectedDate) {
    this.setStatusDate(key, selectedDate);
  }

  onStatusDateTextChange(key) {
    if (this.statusDateTextUpdating[key]) {
      return;
    }

    let text = this.statusDateText[key];
    if (!text || !text.trim()) {
      this.form.statusDates[key] = null;
      this.updateStatusDateMinDates();
      return;
    }

    let parsed = moment(text, ['MMM DD, YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY'], true);
    if (parsed.isValid()) {
      const minDate = this.getStatusDateMinDate(key);
      if (minDate && parsed.isBefore(moment(minDate), 'day')) {
        this.statusDateTextUpdating[key] = true;
        this.statusDateText[key] = this.form.statusDates[key]
          ? moment(this.form.statusDates[key]).format('MMM DD, YYYY')
          : '';
        this.statusDateTextUpdating[key] = false;
        this.toastr.warning(this.getStatusDateOrderMessage(key));
        return;
      }

      this.statusDateTextUpdating[key] = true;
      this.form.statusDates[key] = parsed.format('YYYY-MM-DD');
      this.statusDateText[key] = parsed.format('MMM DD, YYYY');
      this.statusDateTextUpdating[key] = false;
      this.clearInvalidLaterStatusDates(key);
      this.updateStatusDateMinDates();
    }
  }

  setStatusDate(key, selectedDate) {
    let parsed = moment(selectedDate);
    if (!parsed.isValid()) {
      return;
    }

    const minDate = this.getStatusDateMinDate(key);
    if (minDate && parsed.isBefore(moment(minDate), 'day')) {
      this.toastr.warning(this.getStatusDateOrderMessage(key));
      return;
    }

    this.statusDateTextUpdating[key] = true;
    this.form.statusDates[key] = parsed.format('YYYY-MM-DD');
    this.statusDateText[key] = parsed.format('MMM DD, YYYY');
    this.statusDateTextUpdating[key] = false;
    this.clearInvalidLaterStatusDates(key);
    this.updateStatusDateMinDates();
  }

  /**
   * Each status date is constrained only by its immediate predecessor:
   * approved >= submitted
   * reimbursementRequested >= approved
   * reimbursementApproved >= reimbursementRequested
   * processed >= reimbursementApproved
   */
  getStatusDateMinDate(key) {
    const predecessor = {
      approved: 'submitted',
      reimbursementRequested: 'approved',
      reimbursementApproved: 'reimbursementRequested',
      processed: 'reimbursementApproved'
    }[key];

    if (!predecessor) {
      return null;
    }

    return this.form.statusDates[predecessor] || null;
  }

  getStatusDateOrderMessage(key) {
    const messages = {
      approved: 'Approval date must be on or after the submitted date.',
      reimbursementRequested: 'Reimbursement request date must be on or after the travel request approval date.',
      reimbursementApproved: 'Reimbursement approval date must be on or after the reimbursement request date.',
      processed: 'Processed date must be on or after the reimbursement approval date.'
    };
    return messages[key] || 'Status dates must be in chronological order.';
  }

  clearInvalidLaterStatusDates(key) {
    let index = this.statusDateSequence.indexOf(key);
    let current = this.form.statusDates[key];
    if (!current) {
      return;
    }

    for (let i = index + 1; i < this.statusDateSequence.length; i++) {
      let laterKey = this.statusDateSequence[i];
      let later = this.form.statusDates[laterKey];
      if (later && later < current) {
        this.form.statusDates[laterKey] = null;
        this.statusDateText[laterKey] = '';
      }
    }

    this.updateStatusDateMinDates();
  }

  validateDates() {
    for (let i = 0; i < this.statusDateSequence.length; i++) {
      const key = this.statusDateSequence[i];
      const value = this.form.statusDates[key];
      if (!value) {
        continue;
      }
      const minDate = this.getStatusDateMinDate(key);
      if (minDate && value < minDate) {
        return this.getStatusDateOrderMessage(key);
      }
    }

    return null;
  }

  createAndContinue() {
    if (!this.form.userId) {
      this.toastr.warning('Select an employee.');
      return;
    }
    if (!this.form.name || !this.form.name.trim()) {
      this.toastr.warning('Enter a report name.');
      return;
    }
    if (this.form.reportType === 'TRAVEL' && !this.form.purpose) {
      this.toastr.warning('Select a purpose.');
      return;
    }
    if (this.needsProject() && !this.form.project) {
      this.toastr.warning('Select a project.');
      return;
    }
    let dateError = this.validateDates();
    if (dateError) {
      this.toastr.warning(dateError);
      return;
    }

    let self = this;
    this.creating = true;
    let payload = {
      userId: this.form.userId,
      reportType: this.form.reportType,
      name: this.form.name.trim(),
      purpose: this.form.reportType === 'OTHER_DIRECT_COSTS'
        ? 'customer'
        : (this.form.purpose || null),
      project: this.needsProject() ? this.form.project : 0,
      description: this.form.description || null,
      wizardStep: 1,
      statusDates: {
        submitted: this.toDateString(this.form.statusDates.submitted),
        approved: this.toDateString(this.form.statusDates.approved),
        reimbursementRequested: this.toDateString(this.form.statusDates.reimbursementRequested),
        reimbursementApproved: this.toDateString(this.form.statusDates.reimbursementApproved),
        processed: this.toDateString(this.form.statusDates.processed)
      }
    };

    this.$http.post(
      this.$rootScope.apiHost + '/api/admin/expense-reports/on-behalf',
      payload,
      { withCredentials: true }
    ).then(function (res) {
      let report = res.data;
      self.toastr.success('Expense report created. Enter details, then open Review when finished.');
      self.openEditor(report);
    }).catch(function (err) {
      let message = (err.data && (err.data.message || err.data.error)) || 'Could not create expense report.';
      self.toastr.error(message);
    }).finally(function () {
      self.creating = false;
    });
  }

  toDateString(value) {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      return value.substring(0, 10);
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      let month = String(value.getMonth() + 1).padStart(2, '0');
      let day = String(value.getDate()).padStart(2, '0');
      return value.getFullYear() + '-' + month + '-' + day;
    }
    return null;
  }

  openEditor(report) {
    let type = (report.reportType || 'TRAVEL').toUpperCase();
    let params = { expenseReportId: report.id, proxyImport: 'true' };
    if (type === 'GENERAL_BUSINESS') {
      this.$state.go('general-business', params);
    } else if (type === 'OTHER_DIRECT_COSTS') {
      this.$state.go('other-direct-costs', params);
    } else {
      this.$state.go('travel-expense', params);
    }
  }
}

export default ExpenseImportController;



// WEBPACK FOOTER //
// ./client/pages/admin/expense-import/expense-import.controller.js